/* eslint-disable array-element-newline */
/* eslint-disable max-lines-per-function */
/* eslint-disable camelcase */
/* eslint-disable no-invalid-this */
import {action, computed, makeObservable, observable} from 'mobx';
import {processFacetTransformers, processResultFieldTransformers, removeFacets, removeLabGrownFacet, pluckLabGrownFacet} from '../services/middleware';
import {getMappedValue} from 'extensions/utils';
import {DIAMOND_COLOR_MAPPING, DIAMOND_CLARITY_MAPPING, DIAMOND_FLUORESCENCE_MAPPING, DIAMOND_POLISH_MAPPING, DIAMOND_SYMMETRY_MAPPING, DIAMOND_STEP, DIAMOND_CLIENT_GUID, DIAMOND_CLIENT_SEARCH_ENDPOINT, DIAMOND_CLIENT_TRACKING_ENDPOINT} from './constants';
import {ProductStore} from './product-store';

/**
 * @param {import('extensions/hawksearch/services/types').Facet} facet
 */
const stepSliderFacetOverride = (facet, mapping) => {
	// Define custom structure with nouislider options
	facet.custom = {
		...facet.custom,
		noUiSlider: {
			format: {
				from (value) {
					return Math.round(value);
				},
				to (value) {
					return getMappedValue(Math.round(value), mapping, 'facet');
				}
			},
			step: 1,
			values: Object
				.keys(mapping)
				.slice(facet.range.min, facet.range.max + 1)
		}
	};

	// Overwrite existing values
	facet.type = 'step-slider';
	facet.step = 1;

	return facet;
};

export class DiamondStore extends ProductStore {

	type = 'diamond';

	step = DIAMOND_STEP;

	openModal = null;

	labGrown;

	facetTransformers = {
		carat: facet => {
			facet.custom = {
				...facet.custom,
				sliderInputLabel: facet?.label?.replace('Weight', '')
			};
			return facet;
		},
		clarity: facet => {
			return stepSliderFacetOverride(facet, DIAMOND_CLARITY_MAPPING);
		},
		color: facet => {
			return stepSliderFacetOverride(facet, DIAMOND_COLOR_MAPPING);
		},
		fluorescence: facet => {
			return stepSliderFacetOverride(facet, DIAMOND_FLUORESCENCE_MAPPING);
		},
		formatted_shape: facet => {
			facet.type = 'diamond-shape';
			facet.custom = {
				...facet.custom,
				excludeTooltip: true
			};

			return facet;
		},
		hasvideo: facet => {
			facet.type = 'toggle';
			facet.custom = {
				...facet.custom,
				excludeTooltip: true
			};

			return facet;
		},
		inhouse: facet => {
			facet.type = 'toggle';
			facet.custom = {
				...facet.custom,
				excludeTooltip: true
			};

			return facet;
		},
		polish: facet => {
			return stepSliderFacetOverride(facet, DIAMOND_POLISH_MAPPING);
		},

		/**
		 * @param {import('extensions/hawksearch/services/types').Facet} facet
		 */
		price_sale: facet => {
			facet.type = 'checkbox';
			facet.values = [
				{
					title: '$1,000 - $2,500',
					value: '1000,2500'
				},
				{
					title: '$2,500 - $5,000',
					value: '2500,5000'
				},
				{
					title: '$5,000 - $10,000',
					value: '5000,10000'
				},
				{
					title: '$10,000 - $15,000',
					value: '10000,15000'
				},
				{
					title: '$15,000 - $20,000',
					value: '15000,20000'
				},
				{
					title: '$20,000+',
					value: '20000,999999'
				}
			].map(facetValue => ({
				...facetValue,
				selected: facetValue.value === `${facet.range?.start},${facet.range?.end}`
			}));

			facet.custom = {
				...facet.custom,
				excludeTooltip: true
			};

			return facet;
		},
		symmetry: facet => {
			return stepSliderFacetOverride(facet, DIAMOND_SYMMETRY_MAPPING);
		}
	};

	resultFieldTransformers = {
		clarity: value => {
			return getMappedValue(value, DIAMOND_CLARITY_MAPPING, 'result');
		},
		color: value => {
			return getMappedValue(value, DIAMOND_COLOR_MAPPING, 'result');
		},
		fluorescence: value => {
			return getMappedValue(value, DIAMOND_FLUORESCENCE_MAPPING, 'result');
		},
		polish: value => {
			return getMappedValue(value, DIAMOND_POLISH_MAPPING, 'result');
		},
		symmetry: value => {
			return getMappedValue(value, DIAMOND_SYMMETRY_MAPPING, 'result');
		}
	};

	/**
	 * @type {import('extensions/hawksearch/services/types').SearchServiceOptions}
	 */
	searchServiceOptions = {
		...this.searchServiceOptions,
		baseUrl: DIAMOND_CLIENT_SEARCH_ENDPOINT,
		clientGuid: DIAMOND_CLIENT_GUID,
		middleware: {
			...this.searchServiceOptions?.middleware,
			afterSearch: [
				removeFacets(),
				removeLabGrownFacet
			],
			afterStore: [
				...this.searchServiceOptions?.middleware?.afterStore || [],
				processFacetTransformers(this.facetTransformers),
				processResultFieldTransformers(this.resultFieldTransformers),
				pluckLabGrownFacet
			]
		},
		queryStringParamPrefix: 'd-',
		sortingSetCode: 'ringbuilder',
		trackingUrl: DIAMOND_CLIENT_TRACKING_ENDPOINT
	};

	/**
	 * @type {import('extensions/hawksearch/services/types').SearchRequest}
	 */
	get initialProductLoaderFacets () {
		return this.product?.id || this.compareList?.length > 0
			? {
				backgroundFacets: {
					unique_id: [
						...this.product?.id
							? [this.product?.id]
							: [],
						...this.compareList?.length > 0
							? this.compareList
							: []
					]
				}
			}
			: void 0;
	}

	preview = {
		enabled: false,
		measurement: '',
		shape: ''
	};

	reactions = {
		...this.reactions,
		labGrownFacet: () => {
			this.applyBackgroundFacets(this.labGrownFacet);
			this.searchService.search();
		}
	}

	/**
	 * @type {import('extensions/hawksearch/services/types').SelectedFacets}
	 */
	get labGrownFacet () {
		return {
			labgrown: [this.labGrown]
		};
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').SearchResultsItem} settingProduct
	 * @returns {import('extensions/hawksearch/services/types').SearchRequest}
	 */
	compatabilityParams (settingProduct) {
		const fieldName = 'formatted_diamond_value';

		// Prevent fatal error if typeof field is no Array
		if (!Array.isArray(settingProduct?.attributes?.compatible_min_max)) {
			return {};
		}

		const fieldSpecificSearch = settingProduct?.attributes?.compatible_min_max
			?.map(value => {
				const [min, max] = value.split('~');

				return `(${fieldName}: >= ${min} AND ${fieldName}: <= ${max})`;
			})
			?.join(' OR ');

		return {
			fieldSpecificSearch
		};
	}

	constructor ({labGrown, parentStore}) {
		super(parentStore);

		this.searchService = this.createSearchService();
		this.labGrown = labGrown;

		makeObservable(this, {
			labGrown: observable,
			labGrownFacet: computed,
			openModal: observable,
			preview: observable.deep,
			setLabGrown: action,
			setOpenModal: action,
			setPreview: action
		});
	}

	setOpenModal (modal) {
		this.openModal = modal;
	}

	setPreview (preview) {
		this.preview = preview;
	}

	setLabGrown (bool) {
		this.labGrown = Number(bool);
	}

};

